const BLOB_REVOKE_DELAY_MS = 60_000;

const BLOB_EXTENSION_BY_TYPE: Record<string, string> = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/jpeg": "jpg",
};

/** Trigger a browser file download from an in-memory blob. */
export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), BLOB_REVOKE_DELAY_MS);
}

/**
 * Open a blank tab synchronously (must run in a user-gesture handler before any await).
 * Do not pass ``noopener`` in window features — browsers return ``null`` by spec.
 */
export function openBlankTab(): Window | null {
  const popup = window.open("about:blank", "_blank");
  if (popup) {
    popup.opener = null;
  }
  return popup;
}

/** Navigate an already-open tab to an in-memory blob (preview). */
export function showBlobInTab(popup: Window, blob: Blob): void {
  const objectUrl = URL.createObjectURL(blob);
  popup.location.href = objectUrl;
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), BLOB_REVOKE_DELAY_MS);
}

/** Pick a sensible filename extension from a blob's MIME type. */
export function filenameForBlob(baseName: string, blob: Blob): string {
  const ext = BLOB_EXTENSION_BY_TYPE[blob.type] ?? "bin";
  return `${baseName}.${ext}`;
}

/**
 * Fetch a blob then view it in a new tab or download it.
 * Opens the blank tab synchronously before ``fetchBlob`` (required for Safari).
 */
export async function openOrDownload(options: {
  mode: "view" | "download";
  fetchBlob: () => Promise<Blob>;
  baseName: string;
}): Promise<void> {
  const popup = options.mode === "view" ? openBlankTab() : null;
  const mode = options.mode === "view" && !popup ? "download" : options.mode;

  try {
    const blob = await options.fetchBlob();
    const filename = filenameForBlob(options.baseName, blob);
    if (mode === "view" && popup) {
      showBlobInTab(popup, blob);
      return;
    }
    popup?.close();
    downloadBlob(blob, filename);
  } catch (error) {
    popup?.close();
    throw error;
  }
}
